// Plan & Billing für Kunden mit Plan-Payment (preview und enforced) und für
// Admin-Simulationen. Den Umschalter auf LegacyPaymentPlan hat
// actual-payment-plan.js.
//
// Aufbau im Settings-Hausstil, ein SettingsContainer mit Abschnitten aus h4
// und Divider, in dieser Reihenfolge Paketkopf, Nutzung, Funktionen und
// Abrechnung. Keine Preise und kein Vergleich, „Pakete vergleichen"
// verlinkt die Preisseite der Landingpage.
//
// Klickbar sind Zeilen nur in enforced. Limit- und Upgrade-Dialog öffnen
// davor nicht (pricing-dialog-context.js), in preview bleiben die Zeilen
// deshalb statisch und tun nicht so, als ließen sie sich öffnen. Auch in
// enforced öffnet eine Nutzungszeile nur bei endlichem Limit, unbegrenzt
// gibt es nichts anzufragen. „Paketwechsel anfragen" und die Abrechnung sind
// Anfragen über usePricingRequest mit Ergebnis direkt an der Stelle. Admins
// und Simulationen senden auf Production nie, das entscheidet
// pricing-slack.js.
//
// Eine Simulation zeigt das simulierte Paket (entitlements.plan und
// planPayment), die Laufzeit bleibt die echte (periodPayment). Admins sehen
// dazu einen Hinweis mit dem echten Paket.
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid'
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  MinusCircleIcon
} from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../../constants/track-events'
import { usePartnerContext } from '../../../context'
import { MODES } from '../../../features/pricing/entitlements'
import {
  useEntitlements,
  useFeature,
  useLimit,
  useTalentStorage
} from '../../../features/pricing/hooks'
import { LIMIT_STATES } from '../../../features/pricing/limits'
import {
  AVAILABILITY,
  FEATURE_KEYS,
  PLAN_ORDER,
  catalogProductsOf,
  featureAvailability
} from '../../../features/pricing/plans'
import {
  LIMIT_DIALOG_ACTIONS,
  usePricingDialog
} from '../../../features/pricing/pricing-dialog-context'
import {
  PRICING_REQUEST_KINDS,
  PRICING_REQUEST_STATUS
} from '../../../features/pricing/pricing-slack'
import { getPricingCompareUrl } from '../../../features/pricing/use-feature-gate'
import { usePricingRequest } from '../../../features/pricing/use-pricing-request'
import {
  AIVY_MEETING_URL,
  classNames,
  getAivyOsCustomerUrl
} from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import Divider from '../../divider'
import { LockPill, useLockTexts } from '../../pricing/lock-badge'
import UsageMeter, { usageMeterModel } from '../../pricing/usage-meter'
import SEO from '../../seo'
import Badge from '../../tailwind/Badge'
import Button from '../../tailwind/Button'
import InfoCallout from '../../tailwind/InfoCallout'
import SettingsContainer from '../settings-container'

export const PLAN_PAGE_SOURCE = 'plan_page'

const I18N = 'pricing.plan_page.'

// Ressourcen der Nutzung mit ihrem i18n-Block unter pricing.resources
export const USAGE_ROWS = Object.freeze([
  { resource: 'careers', i18nKey: 'careers' },
  { resource: 'seats', i18nKey: 'seats' },
  { resource: 'talentStorage', i18nKey: 'talent_storage' }
])

// ------------------------------------------------------------ reine Helfer

// Gleiche Plausibilitätsregel wie parseTimestamp in helpers/payment-alert.js.
// Vor 2019 gibt es keine Aivy-Verträge, new Date(null) ergäbe 1970.
const PLAUSIBLE_FROM = Date.parse('2019-01-01T00:00:00.000Z')

/**
 * Vertragsende eines Payments in ms, nur bei plausiblem expires_at
 *
 * @param {object|null} payment
 * @returns {number|null}
 */
export const planPeriodEnd = (payment) => {
  const ms = Date.parse(payment?.expires_at)
  if (!Number.isFinite(ms) || ms < PLAUSIBLE_FROM) return null
  return ms
}

/** Datum im Format der Oberfläche, 31. Dezember 2026 bzw. December 31, 2026 */
export const formatPeriodDate = (ms, language) => {
  const locale = String(language || '')
    .toLowerCase()
    .startsWith('en')
    ? 'en-US'
    : 'de-DE'

  return new Date(ms).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

/**
 * Badges im Paketkopf. Aktiv in enforced, sonst Vorschau. Dazu Simulation
 * bei einer Admin-Simulation und Trial, wenn das Payment der Laufzeit ein
 * Trial ist.
 *
 * @returns {{ key: string, color: string }[]}
 */
export const planStatusBadges = (entitlements) => {
  const badges = [
    entitlements?.mode === MODES.ENFORCED
      ? { key: 'status_active', color: 'green' }
      : { key: 'status_preview', color: 'gray' }
  ]
  if (entitlements?.isSimulated) {
    badges.push({ key: 'status_simulated', color: 'yellow' })
  }
  if (entitlements?.periodPayment?.is_trial === true) {
    badges.push({ key: 'status_trial', color: 'blue' })
  }
  return badges
}

export const FEATURE_ROW_STATES = Object.freeze({
  INCLUDED: 'included',
  ADDON_BOOKED: 'addon_booked',
  VIA_INTEGRATION: 'via_integration',
  LOCKED: 'locked',
  PREVIEW_ACTIVE: 'preview_active',
  PREVIEW_UPCOMING: 'preview_upcoming',
  NOT_INCLUDED: 'not_included'
})

/**
 * Zustand einer Funktion auf der Paketseite, rein.
 *
 * Im Paket heißt in enforced freigeschaltet, sonst im Plan-Payment gebucht.
 * Was dann dasteht, liest sich aus dem Katalog wie auf der LP, nicht aus dem
 * Gate. Enthalten ist eine Funktion, wenn ein Katalog-Produkt gebucht ist
 * oder das Paket sie laut Katalog enthält. Gebucht und laut Katalog im Paket
 * nur zubuchbar heißt Zugebucht. Ist das Gate nur über ein Produkt außerhalb
 * des Katalogs frei (CUSTOM_API über INTEGRATIONS oder BROWSERPLUGIN), steht
 * dort neutral „Für deine ATS-Anbindung aktiv", ohne Schloss, denn API-Keys
 * anlegen bleibt erlaubt.
 *
 * In preview sperren die fünf Bestands-Gates noch nach dem Altpayment. Ist
 * eine Funktion im Plan-Payment gebucht, das Altpayment gibt sie aber nicht
 * frei, heißt es neutral „ab dem Start des Pakets". Umgekehrt gibt es kein
 * Schloss, solange das Altpayment sie noch freigibt, dann heißt es „aktuell
 * noch aktiv". Gibt auch das Altpayment sie nicht frei, bleibt es beim
 * Schloss, aber ohne Dialog. Nicht enthalten ist in enforced gesperrt und
 * klickbar.
 *
 * @param {{ featureKey: string, feature: object, mode: string,
 *   planId: string|null, planProducts: string[] }} params
 * @returns {{ state: string, clickable: boolean }}
 */
export const featureRowState = ({
  featureKey,
  feature,
  mode,
  planId,
  planProducts = []
}) => {
  const enforced = mode === MODES.ENFORCED
  const inPackage = enforced
    ? feature?.enabled === true
    : feature?.inPlan === true

  if (inPackage) {
    // Nur Bestands-Gates können in preview gebucht und trotzdem zu sein
    if (!enforced && feature?.enabled !== true) {
      return { state: FEATURE_ROW_STATES.PREVIEW_UPCOMING, clickable: false }
    }

    const availability = PLAN_ORDER.includes(planId)
      ? featureAvailability(featureKey, planId)
      : null
    const catalogBooked = catalogProductsOf(featureKey).some((product) =>
      (planProducts || []).includes(product)
    )

    if (!catalogBooked && availability !== AVAILABILITY.YES) {
      return { state: FEATURE_ROW_STATES.VIA_INTEGRATION, clickable: false }
    }

    return {
      state:
        catalogBooked && availability === AVAILABILITY.ADDON
          ? FEATURE_ROW_STATES.ADDON_BOOKED
          : FEATURE_ROW_STATES.INCLUDED,
      clickable: false
    }
  }

  if (enforced) return { state: FEATURE_ROW_STATES.LOCKED, clickable: true }

  if (feature?.enabled === true) {
    return { state: FEATURE_ROW_STATES.PREVIEW_ACTIVE, clickable: false }
  }

  return { state: FEATURE_ROW_STATES.NOT_INCLUDED, clickable: false }
}

// -------------------------------------------------------------- Anfragen

// Titel und Text nach einer Anfrage, wie in UpgradeInline
const DONE_TEXTS = {
  [PRICING_REQUEST_STATUS.SENT]: {
    title: 'pricing.dialog.sent_title',
    text: 'pricing.dialog.sent_text'
  },
  [PRICING_REQUEST_STATUS.DUPLICATE]: {
    title: 'pricing.dialog.already_sent_title',
    text: 'pricing.dialog.already_sent_text'
  },
  [PRICING_REQUEST_STATUS.DRY_RUN]: {
    title: 'pricing.dialog.admin_dry_run_title',
    text: 'pricing.dialog.admin_dry_run_text'
  }
}

// Anfrage mit Ergebnis an der Stelle. Nach eigenem Klick bekommt das
// Ergebnis den Fokus, denn der Button verschwindet. Startet die Anfrage
// schon in duplicate (im Browser innerhalb von 24 h angefragt), bleibt der
// Fokus, wo er ist.
const useInlineRequest = (params) => {
  const request = usePricingRequest(params)
  const { send: sendRequest } = request
  const statusRef = useRef(null)
  const requestedHereRef = useRef(false)
  const done = DONE_TEXTS[request.status] || null

  useEffect(() => {
    if (done && requestedHereRef.current) statusRef.current?.focus()
  }, [done])

  const send = useCallback(() => {
    requestedHereRef.current = true
    sendRequest()
  }, [sendRequest])

  return { ...request, done, send, statusRef }
}

const TEXT_LINK =
  'inline-flex items-center whitespace-nowrap rounded text-sm font-semibold text-buttonSecondaryBlue hover:text-buttonSecondaryBlue/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'

// Der Fehlertext verweist auf „Termin buchen", deshalb steht der Link direkt
// daneben wie in UpgradeInline
const RequestStatus = ({ request, tracking, className }) => {
  const { t } = useTranslation()

  return (
    <>
      {request.done && (
        <div
          ref={request.statusRef}
          tabIndex={-1}
          role='status'
          className={classNames(
            'flex items-start gap-x-2 rounded focus:outline-none',
            className
          )}
        >
          <CheckCircleIcon
            aria-hidden='true'
            className='size-5 shrink-0 text-green-600'
          />
          <div className='text-sm'>
            <p className='font-medium text-gray-900'>{t(request.done.title)}</p>
            <p className='mt-0.5 text-gray-500'>{t(request.done.text)}</p>
          </div>
        </div>
      )}
      {request.isError && (
        <div
          className={classNames(
            'flex flex-wrap items-center gap-x-3 gap-y-1',
            className
          )}
        >
          <p role='alert' className='text-sm text-red-600'>
            {t('pricing.dialog.error_text')}
          </p>
          <a
            href={AIVY_MEETING_URL}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() =>
              trackEvent(trackEvents.PRICING_MEETING_PRESSED, tracking)
            }
            className={TEXT_LINK}
          >
            {t('pricing.dialog.meeting_action')}
          </a>
        </div>
      )}
      <p className='sr-only' aria-live='polite'>
        {request.isSending ? t('pricing.dialog.sending_text') : ''}
      </p>
    </>
  )
}

RequestStatus.propTypes = {
  request: PropTypes.shape({
    done: PropTypes.shape({ title: PropTypes.string, text: PropTypes.string }),
    statusRef: PropTypes.object,
    isError: PropTypes.bool,
    isSending: PropTypes.bool
  }).isRequired,
  tracking: PropTypes.object.isRequired,
  className: PropTypes.string
}

RequestStatus.defaultProps = { className: undefined }

// ------------------------------------------------------------------ Zeilen

// Ganze Zeile klickbar über einen gestreckten Button am Namen. So bleibt der
// Meter außerhalb des Buttons und behält seine Rolle.
const STRETCHED_BUTTON =
  'text-left focus:outline-none after:absolute after:-inset-x-2 after:inset-y-0 after:rounded-md focus-visible:after:ring-2 focus-visible:after:ring-blue-600'

const RowChevron = () => (
  <ChevronRightIcon
    aria-hidden='true'
    className='hidden size-5 flex-none text-gray-300 group-hover:text-gray-700 sm:block'
  />
)

// Platzhalter in der Breite des Chevrons, damit die Meter untereinander
// bleiben, wenn nur ein Teil der Zeilen klickbar ist
const ChevronSpace = () => (
  <span aria-hidden='true' className='hidden size-5 flex-none sm:block' />
)

// Zustand als Text unter dem Meter, nicht nur als Farbe (WCAG 1.4.1). Farben
// wie im UsageMeter in Größe md.
const STATE_TEXT = {
  [LIMIT_STATES.WARNING]: 'text-yellow-800',
  [LIMIT_STATES.AT_LIMIT]: 'text-red-700',
  [LIMIT_STATES.OVER_LIMIT]: 'text-red-700'
}

/** Ein Nutzungslimit öffnet den Limit-Dialog nur, wenn es endlich ist */
export const isUsageRowInteractive = ({ enforced, limit }) =>
  !!enforced && typeof limit === 'number' && Number.isFinite(limit)

const UsageRow = ({
  resource,
  i18nKey,
  used,
  limit,
  failed,
  interactive,
  reserveChevron,
  onOpen
}) => {
  const { t } = useTranslation()
  const definitionId = useId()
  const name = t(`pricing.resources.${i18nKey}.name`)
  const { state } = usageMeterModel({ used, limit })
  const stateClass = failed ? null : STATE_TEXT[state]

  return (
    <li
      data-resource={resource}
      className={classNames(
        'relative flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:gap-x-6',
        interactive && 'group'
      )}
    >
      <div className='min-w-0 flex-1'>
        {interactive ? (
          <button
            type='button'
            aria-haspopup='dialog'
            aria-describedby={definitionId}
            onClick={onOpen}
            className={classNames(
              STRETCHED_BUTTON,
              'text-sm font-medium text-gray-900'
            )}
          >
            {name}
          </button>
        ) : (
          <p className='text-sm font-medium text-gray-900'>{name}</p>
        )}
        <p id={definitionId} className='mt-0.5 text-sm text-gray-500'>
          {/* Auf der Übersicht reicht beim Talentspeicher die Kurzfassung,
              die volle Definition steht im Limit-Dialog. Key-Array statt
              defaultValue, sonst meldet saveMissing die fehlende Kurzfassung */}
          {t([
            `pricing.resources.${i18nKey}.summary`,
            `pricing.resources.${i18nKey}.definition`
          ])}
        </p>
      </div>
      <div className='w-full sm:w-48 sm:shrink-0'>
        <UsageMeter used={used} limit={limit} label={name} failed={failed} />
        {stateClass && (
          <p className={classNames('mt-1 text-xs font-medium', stateClass)}>
            {t(`pricing.usage.state_${state}`)}
          </p>
        )}
      </div>
      {interactive ? <RowChevron /> : reserveChevron && <ChevronSpace />}
    </li>
  )
}

UsageRow.propTypes = {
  resource: PropTypes.string.isRequired,
  i18nKey: PropTypes.string.isRequired,
  used: PropTypes.number,
  limit: PropTypes.number,
  failed: PropTypes.bool,
  interactive: PropTypes.bool,
  reserveChevron: PropTypes.bool,
  onOpen: PropTypes.func
}

UsageRow.defaultProps = {
  used: null,
  limit: null,
  failed: false,
  interactive: false,
  reserveChevron: false,
  onOpen: undefined
}

// Plan & Billing lädt die Nutzung auch in preview (fetch true)
const LimitUsageRow = ({ resource, i18nKey, enforced, onOpen }) => {
  const limit = useLimit(resource, { fetch: true })

  return (
    <UsageRow
      resource={resource}
      i18nKey={i18nKey}
      used={limit.used}
      limit={limit.limit}
      failed={limit.status === 'error'}
      interactive={isUsageRowInteractive({ enforced, limit: limit.limit })}
      reserveChevron={enforced}
      onOpen={onOpen}
    />
  )
}

LimitUsageRow.propTypes = {
  resource: PropTypes.string.isRequired,
  i18nKey: PropTypes.string.isRequired,
  enforced: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired
}

// Talentspeicher je Person über den Cache der Talentliste. Auf dieser Seite
// ist die schwere Talents-Query akzeptiert.
const TalentStorageUsageRow = ({ i18nKey, enforced, onOpen }) => {
  const storage = useTalentStorage({ fetch: true })

  return (
    <UsageRow
      resource='talentStorage'
      i18nKey={i18nKey}
      used={storage.used}
      limit={storage.limit}
      failed={storage.status === 'error'}
      interactive={isUsageRowInteractive({ enforced, limit: storage.limit })}
      reserveChevron={enforced}
      onOpen={onOpen}
    />
  )
}

TalentStorageUsageRow.propTypes = {
  i18nKey: PropTypes.string.isRequired,
  enforced: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired
}

const NEUTRAL_STATUS = {
  [FEATURE_ROW_STATES.PREVIEW_ACTIVE]: {
    icon: MinusCircleIcon,
    text: 'preview_not_included'
  },
  [FEATURE_ROW_STATES.PREVIEW_UPCOMING]: {
    icon: ClockIcon,
    text: 'preview_upcoming'
  },
  [FEATURE_ROW_STATES.VIA_INTEGRATION]: {
    icon: CheckCircleIcon,
    text: 'via_integration'
  }
}

const FeatureStatus = ({ state, lockLabel, lockTitle, id }) => {
  const { t } = useTranslation()

  if (
    state === FEATURE_ROW_STATES.INCLUDED ||
    state === FEATURE_ROW_STATES.ADDON_BOOKED
  ) {
    return (
      <span id={id} className='inline-flex items-center gap-x-1.5'>
        <CheckCircleIcon
          aria-hidden='true'
          className='size-5 shrink-0 text-green-600'
        />
        {t(
          I18N +
            (state === FEATURE_ROW_STATES.ADDON_BOOKED
              ? 'addon_booked'
              : 'included')
        )}
      </span>
    )
  }

  // Neutrale Zustände ohne Schloss und ohne grünes Häkchen
  const neutral = NEUTRAL_STATUS[state]
  if (neutral) {
    const Icon = neutral.icon
    return (
      <span
        id={id}
        className='inline-flex items-start gap-x-1.5 sm:items-center'
      >
        <Icon aria-hidden='true' className='size-5 shrink-0 text-gray-400' />
        {t(I18N + neutral.text)}
      </span>
    )
  }

  return (
    <span id={id} className='inline-flex items-center gap-x-2'>
      <span title={lockTitle}>
        <LockPill label={lockLabel} />
      </span>
      {t(I18N + 'not_included')}
    </span>
  )
}

FeatureStatus.propTypes = {
  state: PropTypes.oneOf(Object.values(FEATURE_ROW_STATES)).isRequired,
  lockLabel: PropTypes.string.isRequired,
  lockTitle: PropTypes.string,
  id: PropTypes.string.isRequired
}

FeatureStatus.defaultProps = { lockTitle: undefined }

const FeatureRow = ({ featureKey, planId, planProducts }) => {
  const feature = useFeature(featureKey)
  const { gate, label, includedText, title } = useLockTexts(featureKey, {
    source: PLAN_PAGE_SOURCE
  })
  const statusId = useId()
  const { state, clickable } = featureRowState({
    featureKey,
    feature,
    mode: feature.mode,
    planId,
    planProducts
  })

  return (
    <li
      data-feature={featureKey}
      data-state={state}
      className={classNames(
        'relative flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-x-6',
        clickable && 'group'
      )}
    >
      {clickable ? (
        <button
          type='button'
          aria-haspopup='dialog'
          aria-describedby={statusId}
          onClick={gate.open}
          className={classNames(
            STRETCHED_BUTTON,
            'min-w-0 text-sm font-medium text-gray-900'
          )}
        >
          {title}
        </button>
      ) : (
        <p className='min-w-0 text-sm font-medium text-gray-900'>{title}</p>
      )}
      <div className='flex items-center gap-x-6 text-sm text-gray-700 sm:shrink-0 sm:text-right'>
        <FeatureStatus
          state={state}
          lockLabel={label}
          lockTitle={includedText}
          id={statusId}
        />
        {clickable && <RowChevron />}
      </div>
    </li>
  )
}

FeatureRow.propTypes = {
  featureKey: PropTypes.oneOf(FEATURE_KEYS).isRequired,
  planId: PropTypes.string,
  planProducts: PropTypes.arrayOf(PropTypes.string).isRequired
}

FeatureRow.defaultProps = { planId: null }

// ------------------------------------------------------------- Abschnitte

const SectionHeading = ({ id, title, description }) => (
  <>
    <h4 id={id} className='text-sm font-medium leading-6 text-gray-900'>
      {title}
    </h4>
    {description && <p className='text-sm text-gray-500'>{description}</p>}
  </>
)

SectionHeading.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string
}

SectionHeading.defaultProps = { description: undefined }

const COMPARE_LINK =
  'inline-flex items-center justify-center gap-x-1 whitespace-nowrap rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'

const adminPlanName = (plan) =>
  plan?.isCustom ? 'Individuell' : plan?.name || plan?.id || null

/**
 * Satz des Admin-Hinweises je Fall, rein. Admin-Texte bleiben wie in den
 * übrigen Admin-Werkzeugen fest Deutsch.
 */
export const adminSimulationText = ({ plan, realPlan, realPlanPayment }) => {
  const simulated = `Du simulierst ${adminPlanName(plan) || 'ein Paket'}`
  const real = adminPlanName(realPlan)

  if (real) return `${simulated}, das echte Paket ist ${real}.`
  if (realPlanPayment) {
    return `${simulated}. Das echte Plan-Payment lässt sich keinem Paket zuordnen.`
  }
  return `${simulated}. Ein echtes Paket ist nicht hinterlegt.`
}

// Nur für Aivy-Admins während einer Simulation
const AdminSimulationNote = ({ entitlements }) => {
  const { partner } = usePartnerContext()

  return (
    <p data-admin-note='' className='text-xs text-gray-500'>
      {`Nur für Aivy-Admins sichtbar. ${adminSimulationText(entitlements)}`}
      {partner?.id && (
        <>
          {' '}
          <a
            href={getAivyOsCustomerUrl({ partner_id: partner.id })}
            target='_blank'
            rel='noopener noreferrer'
            className='font-semibold text-primaryBlue hover:text-primaryBlue/80'
          >
            Im Aivy OS öffnen
          </a>
        </>
      )}
    </p>
  )
}

AdminSimulationNote.propTypes = {
  entitlements: PropTypes.shape({
    realPlan: PropTypes.object,
    realPlanPayment: PropTypes.object,
    plan: PropTypes.object
  }).isRequired
}

const PlanOverview = () => {
  const { t, i18n } = useTranslation()
  const entitlements = useEntitlements()
  const { openLimitDialog } = usePricingDialog()
  const headId = useId()
  const planNameId = useId()
  const usageId = useId()
  const featuresId = useId()
  const billingId = useId()

  const { mode, plan, isSimulated, isAdmin } = entitlements
  const enforced = mode === MODES.ENFORCED
  const planId = plan?.id ?? null
  const planName = plan?.isCustom
    ? t('pricing.plans.custom')
    : plan?.name || (planId ? t(`pricing.plans.${planId}`) : '')
  const planProducts = entitlements.planPayment?.products || []
  const periodEnd = planPeriodEnd(entitlements.periodPayment)

  const planRequest = useInlineRequest({
    kind: PRICING_REQUEST_KINDS.PLAN,
    source: PLAN_PAGE_SOURCE
  })
  const billingRequest = useInlineRequest({
    kind: PRICING_REQUEST_KINDS.BILLING,
    source: PLAN_PAGE_SOURCE
  })

  // Tracking für „Termin buchen" neben einem Fehler
  const trackingOf = (kind) => ({
    view: PLAN_PAGE_SOURCE,
    kind,
    source: PLAN_PAGE_SOURCE,
    plan: planId,
    mode
  })

  const openUsage = (resource) =>
    openLimitDialog(resource, {
      action: LIMIT_DIALOG_ACTIONS.INFO,
      source: PLAN_PAGE_SOURCE
    })

  return (
    <SettingsContainer
      title={t('components.settings.payment_plan.title')}
      description={t('components.settings.payment_plan.description')}
    >
      <SEO title={t('seo.titles.settings_planbilling')} />

      <section
        aria-labelledby={`${headId} ${planNameId}`}
        className='space-y-4'
      >
        <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
          <div className='min-w-0'>
            <h4
              id={headId}
              className='text-sm font-medium leading-6 text-gray-900'
            >
              {t(I18N + 'your_plan')}
            </h4>
            <div className='mt-1 flex flex-wrap items-center gap-x-3 gap-y-1'>
              <p
                id={planNameId}
                className='text-lg font-semibold leading-7 text-gray-900'
              >
                {planName}
              </p>
              {planStatusBadges(entitlements).map(({ key, color }) => (
                <Badge key={key} text={t(I18N + key)} color={color} />
              ))}
            </div>
            {plan?.isCustom && (
              <p className='mt-1 text-sm text-gray-500'>
                {t(I18N + 'custom_hint')}
              </p>
            )}
            {periodEnd !== null && (
              <p className='mt-1 text-sm text-gray-500'>
                {t(I18N + 'period', {
                  date: formatPeriodDate(periodEnd, i18n?.language)
                })}
              </p>
            )}
          </div>
          <div className='flex flex-col gap-2 sm:shrink-0 sm:items-end'>
            <div className='flex flex-wrap items-center gap-3'>
              <a
                href={getPricingCompareUrl(i18n?.language)}
                target='_blank'
                rel='noopener noreferrer'
                className={COMPARE_LINK}
              >
                {t(I18N + 'compare_action')}
                <ArrowTopRightOnSquareIcon
                  aria-hidden='true'
                  className='size-4'
                />
              </a>
              {!planRequest.done && (
                <Button.PrimaryLG
                  text={t(I18N + 'change_action')}
                  onClick={planRequest.send}
                  isLoading={planRequest.isSending}
                />
              )}
            </div>
            {/* Vor dem Klick klar, dass eine Anfrage rausgeht */}
            {!planRequest.done && !planRequest.isError && (
              <p
                data-request-hint=''
                className='text-sm text-gray-500 sm:max-w-xs sm:text-right'
              >
                {t('pricing.dialog.request_hint')}
              </p>
            )}
          </div>
        </div>
        <RequestStatus
          request={planRequest}
          tracking={trackingOf(PRICING_REQUEST_KINDS.PLAN)}
        />
        {mode === MODES.PREVIEW && (
          <InfoCallout>{t(I18N + 'preview_hint')}</InfoCallout>
        )}
        {isAdmin && isSimulated && (
          <AdminSimulationNote entitlements={entitlements} />
        )}
      </section>

      <Divider />

      <section aria-labelledby={usageId}>
        <SectionHeading
          id={usageId}
          title={t(I18N + 'usage_title')}
          description={t(I18N + 'usage_description')}
        />
        <ul role='list' className='mt-4 divide-y divide-gray-200'>
          {USAGE_ROWS.map(({ resource, i18nKey }) =>
            resource === 'talentStorage' ? (
              <TalentStorageUsageRow
                key={resource}
                i18nKey={i18nKey}
                enforced={enforced}
                onOpen={() => openUsage(resource)}
              />
            ) : (
              <LimitUsageRow
                key={resource}
                resource={resource}
                i18nKey={i18nKey}
                enforced={enforced}
                onOpen={() => openUsage(resource)}
              />
            )
          )}
          <li
            data-resource='tests'
            className='flex flex-col gap-1 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:gap-x-6'
          >
            <p className='min-w-0 flex-1 text-sm font-medium text-gray-900'>
              {t('pricing.resources.tests.name')}
            </p>
            <p className='text-sm text-gray-700 sm:w-48 sm:shrink-0 sm:text-right'>
              {t('pricing.resources.tests.value')}
            </p>
            {enforced && <ChevronSpace />}
          </li>
        </ul>
      </section>

      <Divider />

      <section aria-labelledby={featuresId}>
        <SectionHeading id={featuresId} title={t(I18N + 'features_title')} />
        <ul role='list' className='mt-4 divide-y divide-gray-200'>
          {FEATURE_KEYS.map((featureKey) => (
            <FeatureRow
              key={featureKey}
              featureKey={featureKey}
              planId={planId}
              planProducts={planProducts}
            />
          ))}
        </ul>
        <p className='mt-4 text-sm text-gray-500'>
          {t(I18N + 'always_included')}
        </p>
      </section>

      <Divider />

      <section aria-labelledby={billingId}>
        <SectionHeading
          id={billingId}
          title={t(I18N + 'billing_title')}
          description={t(I18N + 'billing_text')}
        />
        {!billingRequest.done && (
          <Button.Text
            text={t(I18N + 'billing_action')}
            onClick={billingRequest.send}
            isLoading={billingRequest.isSending}
            minWidth=''
            className='mt-3'
          />
        )}
        <RequestStatus
          request={billingRequest}
          tracking={trackingOf(PRICING_REQUEST_KINDS.BILLING)}
          className='mt-3'
        />
      </section>
    </SettingsContainer>
  )
}

export default PlanOverview
